import classNames from "classnames/bind";
import React, { useEffect, useState } from "react";
import { Button } from "../Button";
import FormInputWrapper from "../Form/FormInputs/FormInputWrapper";
import styles from "./FilterSwitcher.module.scss";
import type {
  ControlledFilterSwitcherProps,
  FilterSwitcherProps,
} from "./types";

const cx = classNames.bind(styles);

/**
 * A filter component presented as a context switcher
 */
const FilterSwitcher = ({
  label,
  id,
  name,
  value,
  getDisplayValue,
  onEdit,
  isEditable = true,
  isInline = false,
  isDisabled = false,
}: FilterSwitcherProps) => {
  const [displayValue, setDisplayValue] = useState(value);

  useEffect(() => {
    setDisplayValue(getDisplayValue ? getDisplayValue(value) : value);
  }, [value]);

  const handleEditClick = () => {
    onEdit?.(value ?? "");
  };

  const labelId = `${id}-label`;

  return (
    <div
      className={cx(
        "container",
        isInline && "container--isInline",
        isEditable && "container--editable"
      )}
      role="combobox"
      aria-disabled={isDisabled}
      aria-labelledby={labelId}
    >
      <div className={cx("displayContent")}>
        <label
          className={cx("label")}
          htmlFor={id}
          id={labelId}
        >
          {label}
        </label>
        <input
          id={id}
          value={value}
          name={name}
          type="text"
          inputMode="none"
          className={cx("input")}
          readOnly
          hidden
          aria-hidden={false}
          disabled={isDisabled}
        />
        <p
          className={cx("inputDisplay")}
          aria-labelledby={labelId}
        >
          {displayValue?.trim() !== "" ? displayValue : "-"}
        </p>
      </div>
      {isEditable ? (
        <>
          <Button
            className={cx("button")}
            style={{ padding: 4 }}
            type="button"
            variant="text"
            icon="swap"
            onClick={handleEditClick}
            aria-controls={id}
            aria-label={`Edit ${name}`}
            disabled={isDisabled}
          />
        </>
      ) : null}
    </div>
  );
};

const ControlledFilterSwitcher = (props: ControlledFilterSwitcherProps) => (
  <FormInputWrapper
    {...props}
    isLabelShown={false}
    as={FilterSwitcher}
  />
);

export default FilterSwitcher;
export { ControlledFilterSwitcher };
